// src/components/marketing/NavigationWithAuth.tsx
import { Link, useLocation } from 'react-router-dom';
import { colors } from '../../lib/colors';
import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContextLazy';

// Use the public path for the logo
const logoPath = '/assets/Brands/PCM Logo White.png';

export default function NavigationWithAuth() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;
  const { user, userProfile } = useAuth();

  const buttonStyle = {
    background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
    boxShadow: `0 6px 18px -6px ${colors.brand.primary}80`,
    color: '#fff'
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <nav className="w-full bg-premium-dark text-text-light py-3 border-b border-gray-800/50 relative z-50">
      <div className="container max-w-7xl mx-auto px-3">
        {/* Desktop Layout - Grid with 3 columns */}
        <div className="hidden md:grid md:grid-cols-[auto_1fr_auto] items-center">
          {/* Left - Logo */}
          <div className="flex justify-start">
            <Link to="/">
              <img src={logoPath} alt="PCM Logo" className="h-12 md:h-16 w-auto" />
            </Link>
          </div>
          
          {/* Center - Main Navigation */}
          <div className="flex items-center justify-center text-sm gap-4 whitespace-nowrap">
            <Link 
              to="/" 
              className={`hover:text-brand-accent transition-colors relative ${currentPath === '/' ? 'text-brand-accent font-medium' : ''}`}
            >
              Home
              {currentPath === '/' && <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-brand-accent rounded-full"></span>}
            </Link>
            <Link 
              to="/about" 
              className={`hover:text-brand-accent transition-colors relative ${currentPath === '/about' ? 'text-brand-accent font-medium' : ''}`}
            >
              About
              {currentPath === '/about' && <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-brand-accent rounded-full"></span>}
            </Link>
            
            {/* Services Dropdown */}
            <div className="relative group">
              <Link 
                to="/services" 
                className={`hover:text-brand-accent transition-colors relative ${currentPath.startsWith('/services') || currentPath === '/family-insurance' || currentPath === '/business-insurance' ? 'text-brand-accent font-medium' : ''}`}
              >
                Services
                {(currentPath.startsWith('/services') || currentPath === '/family-insurance' || currentPath === '/business-insurance') && <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-brand-accent rounded-full"></span>}
              </Link>
              {/* Dropdown Menu */}
              <div className="absolute top-full left-0 mt-2 w-56 bg-premium-darker rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <Link to="/family-insurance" className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50">
                    Family Protection Center
                  </Link>
                  <Link to="/business-insurance" className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50">
                    Business Owner Command
                  </Link>
                  <div className="border-t border-gray-700/50 my-2"></div>
                  <Link to="/private-advisory/process" className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50">
                    Private Advisory Process
                  </Link>
                  <div className="border-t border-gray-700/50 my-2"></div>
                  <Link to="/services/term-life-insurance" className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50">
                    Life Insurance
                  </Link>
                  <Link to="/services/disability-insurance" className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50">
                    Disability Insurance
                  </Link>
                  <Link to="/services" className="block px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800/50 italic">
                    View All Services →
                  </Link>
                </div>
              </div>
            </div>
            
            <Link 
              to="/locations" 
              className={`hover:text-brand-accent transition-colors relative ${currentPath.startsWith('/locations') ? 'text-brand-accent font-medium' : ''}`}
            >
              Locations
              {currentPath.startsWith('/locations') && <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-brand-accent rounded-full"></span>}
            </Link>
            
            <Link 
              to="/case-studies" 
              className={`hover:text-brand-accent transition-colors relative ${currentPath === '/case-studies' ? 'text-brand-accent font-medium' : ''}`}
            >
              Case Studies
              {currentPath === '/case-studies' && <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-brand-accent rounded-full"></span>}
            </Link>
            <Link 
              to="/blog" 
              className={`hover:text-brand-accent transition-colors relative ${currentPath === '/blog' ? 'text-brand-accent font-medium' : ''}`}
            >
              Blog
              {currentPath === '/blog' && <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-brand-accent rounded-full"></span>}
            </Link>
            <Link 
              to="/contact"
              style={buttonStyle}
              className="px-3 py-2 rounded-md transition-all hover:opacity-90 text-sm inline-block whitespace-nowrap"
            >
              Contact Us
            </Link>
          </div>
          
          {/* Right - Login/Portal */}
          <div className="flex justify-end items-center">
            {user ? (
              <Link 
                to={userProfile?.role === 'client_user' ? '/client-portal' : '/portal'} 
                className="text-amber-400 hover:text-amber-300 transition-colors text-sm flex items-center gap-2 font-medium px-2 py-1 rounded-md hover:bg-amber-500/10"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {userProfile?.role === 'client_user' ? 'Client Portal' : 'Portal'}
                {userProfile?.role === 'super_admin' && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500/20 text-amber-300 rounded">Admin</span>
                )}
              </Link>
            ) : (
              <div className="flex items-center divide-x divide-gray-700/50">
                <Link 
                  to="/client-portal/login" 
                  className="text-gray-300 hover:text-white transition-colors text-sm flex items-center gap-2 px-3 py-1 rounded-md hover:bg-gray-800/30"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Client Portal
                </Link>
                <Link 
                  to="/login" 
                  className="text-gray-300 hover:text-white transition-colors text-sm flex items-center gap-2 px-3 py-1 ml-3 pl-6 rounded-md hover:bg-gray-800/30"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Advisor Login
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Layout - Flex between logo and hamburger */}
        <div className="flex md:hidden justify-between items-center">
          <Link to="/">
            <img src={logoPath} alt="PCM Logo" className="h-12 w-auto" />
          </Link>
          
          <button 
            className="flex flex-col justify-center items-center p-2"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <span className={`block w-6 h-0.5 bg-white mb-1.5 transition-transform duration-300 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`block w-6 h-0.5 bg-white mb-1.5 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
            <span className={`block w-6 h-0.5 bg-white transition-transform duration-300 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </button>
        </div>
      </div>

      {/* Mobile Menu - Slides in from top when open */}
      <div 
        className={`absolute top-full left-0 right-0 bg-premium-darker shadow-lg transition-all duration-300 ease-in-out overflow-hidden ${mobileMenuOpen ? 'max-h-[calc(100vh-4rem)] border-b border-gray-700' : 'max-h-0'}`}
      >
        <div className="container max-w-4xl mx-auto px-4 py-2 max-h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="flex flex-col space-y-4 py-3">
            <Link 
              to="/" 
              className={`text-white hover:text-brand-accent transition-colors py-2 border-b border-gray-700/50 ${currentPath === '/' ? 'text-brand-accent pl-2 border-brand-accent/50' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className={`text-white hover:text-brand-accent transition-colors py-2 border-b border-gray-700/50 ${currentPath === '/about' ? 'text-brand-accent pl-2 border-brand-accent/50' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              About
            </Link>
            <div className="space-y-2">
              <Link 
                to="/services" 
                className={`text-white hover:text-brand-accent transition-colors py-2 block ${currentPath === '/services' ? 'text-brand-accent' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Services
              </Link>
              <div className="pl-4 space-y-2">
                <Link 
                  to="/family-insurance" 
                  className={`text-gray-300 hover:text-brand-accent transition-colors py-1 block text-sm ${currentPath === '/family-insurance' ? 'text-brand-accent' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  → Family Protection
                </Link>
                <Link 
                  to="/business-insurance" 
                  className={`text-gray-300 hover:text-brand-accent transition-colors py-1 block text-sm ${currentPath === '/business-insurance' ? 'text-brand-accent' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  → Business Owners
                </Link>
                <Link 
                  to="/private-advisory/process" 
                  className={`text-gray-300 hover:text-brand-accent transition-colors py-1 block text-sm ${currentPath === '/private-advisory/process' ? 'text-brand-accent' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  → Advisory Process
                </Link>
              </div>
            </div>
            <Link 
              to="/locations" 
              className={`text-white hover:text-brand-accent transition-colors py-2 border-b border-gray-700/50 ${currentPath.startsWith('/locations') ? 'text-brand-accent pl-2 border-brand-accent/50' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Locations
            </Link>
            <Link 
              to="/case-studies" 
              className={`text-white hover:text-brand-accent transition-colors py-2 border-b border-gray-700/50 ${currentPath === '/case-studies' ? 'text-brand-accent pl-2 border-brand-accent/50' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Case Studies
            </Link>
            <Link 
              to="/blog" 
              className={`text-white hover:text-brand-accent transition-colors py-2 border-b border-gray-700/50 ${currentPath === '/blog' ? 'text-brand-accent pl-2 border-brand-accent/50' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </Link>
            <Link 
              to="/contact" 
              className={`text-white hover:text-brand-accent transition-colors py-2 border-b border-gray-700/50 ${currentPath === '/contact' ? 'text-brand-accent pl-2 border-brand-accent/50' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact Us
            </Link>
            {user ? (
              <Link 
                to={userProfile?.role === 'client_user' ? '/client-portal' : '/portal'} 
                className="text-amber-400 hover:text-amber-300 transition-colors py-2 border-t border-gray-700/50 flex items-center gap-2 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {userProfile?.role === 'client_user' ? 'Client Portal' : 'Portal'}
                {userProfile?.role === 'super_admin' && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500/20 text-amber-300 rounded">Admin</span>
                )}
              </Link>
            ) : (
              <div className="border-t border-gray-700/50 pt-2 space-y-2">
                <Link 
                  to="/client-portal/login" 
                  className="text-gray-300 hover:text-white transition-colors py-2 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Client Portal
                </Link>
                <Link 
                  to="/login" 
                  className="text-gray-300 hover:text-white transition-colors py-2 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Advisor Login
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}